import { RunStatus, ScheduleResponse, ScheduleTaskStatus } from '@/app/schedule/_utils/types'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import type { Pagination } from '@/hooks/api/_types'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

type ScheduleTasksAPIResponse = {
  items: ScheduleResponse[]
  total: number
  page: number
  size: number
  pages: number
}

type TransformedScheduleTasksAPIResponse = {
  data: ScheduleResponse[]
  pagination: Pagination
}

export type UseScheduleTasksSearchParams = {
  search_query?: string
  sort?: 'agent' | 'start_time' | 'end_time' | 'frequency' | 'status' | 'last_run_status'
  order?: 'asc' | 'desc'
  agent_id?: string
  status?: ScheduleTaskStatus
  last_run_status?: RunStatus
  start_time_after?: string
  end_time_before?: string
  active?: boolean
  page?: number
  size?: number
}

type UseScheduleTasksQueryOptions = {
  searchParams?: UseScheduleTasksSearchParams
  queryOptions?: Omit<UseQueryOptions<TransformedScheduleTasksAPIResponse>, 'queryFn' | 'queryKey'>
}

function useScheduleTasksQuery({
  searchParams = {},
  queryOptions = {},
}: UseScheduleTasksQueryOptions = {}) {
  const { apiRequest, domainId } = useApiRequest()
  const urlSearchString = new URLSearchParams(
    portSearchParams({
      page: 1,
      size: 10,
      ...searchParams,
    }),
  ).toString()

  return useQuery({
    queryKey: scheduleTasksKeyFactory({ params: searchParams, domainId }),
    queryFn: async () => {
      const response = await apiRequest({
        path: `${taskManagerApiPaths.schedules({ domainId })}?${urlSearchString}`,
      })

      if (response.status === 404) {
        const errorData = await response.json()
        if (errorData?.detail === 'No schedules found') {
          return {
            data: [],
            pagination: {
              page: 1,
              limit: searchParams.size ?? 10,
              totalItems: 0,
              totalPages: 0,
              nextPage: 1,
              prevPage: 1,
            },
          }
        }
      }

      if (!response.ok) throw new Error('FAILED_TO_GET_SCHEDULE_TASKS')

      const json = (await response.json()) as ScheduleTasksAPIResponse

      const transformedResponse: TransformedScheduleTasksAPIResponse = {
        data: json.items,
        pagination: {
          page: json.page,
          limit: json.size,
          totalItems: json.total,
          totalPages: json.pages,
          nextPage: json.page < json.pages ? json.page + 1 : 1,
          prevPage: json.page > 1 ? json.page - 1 : 1,
        },
      }

      return transformedResponse
    },

    ...queryOptions,
  })
}

const portSearchParams = (params: UseScheduleTasksSearchParams): Record<string, string> =>
  Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (typeof value === 'undefined' || value === null) {
        return acc
      }
      const valueString = `${value}`
      if (!valueString) return acc
      return { ...acc, [key]: valueString }
    },
    {} as Record<string, string>,
  )

const scheduleTasksKeyFactory = ({
  params = {},
  domainId,
}: {
  params?: UseScheduleTasksSearchParams
  domainId: string
}) => ['schedule-tasks', domainId, params]

function updateKeysInScheduleTasksCache({
  cachedData,
  taskId,
  updateData,
}: {
  cachedData?: TransformedScheduleTasksAPIResponse
  taskId: string
  updateData: Partial<ScheduleResponse>
}) {
  if (!cachedData || !cachedData?.data || cachedData.data?.length === 0) {
    return cachedData
  }

  return {
    ...cachedData,
    data: cachedData.data.map((task) => {
      if (task.id === taskId) {
        return {
          ...task,
          ...updateData,
        }
      }
      return task
    }),
  }
}

const isScheduleTaskActive = (task: ScheduleResponse) => {
  return task.status === ScheduleTaskStatus.Active
}

export {
  isScheduleTaskActive,
  scheduleTasksKeyFactory,
  updateKeysInScheduleTasksCache,
  useScheduleTasksQuery,
  type ScheduleTasksAPIResponse,
  type TransformedScheduleTasksAPIResponse,
}
